import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import RadioButtonListField from "../../../components/Fields/RadioButtonListField";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemRevisionModel from "../../../models/ItemRevisionModel";
import DataService from "../../../services/data.service";
import { DataResult } from "../../../types/Result";

interface ItemAttachmentGenerateModel {
    billOfMaterialsId: number
    attachmentTypeId: number
    language: string
    documentOutputTypeId: number
    outputOptions: number
}

interface Props {
    show: boolean
    onHide: () => void
    itemRevision: ItemRevisionModel
}

const GenerateAttachmentPopup: React.FC<Props> = ({ show, onHide, itemRevision }) => {
    const [serverErrors, setServerErrors] = useState<DataResult<ItemAttachmentGenerateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const [showSpinner, setShowSpinner] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { user } = useAuth();

    const boms = useSelectListOptions('api/v1/selectlist/BillOfMaterials?itemId=' + itemRevision.itemId);
    const attachmentTypes = useSelectListOptions('api/v1/selectlist/ItemRevisionGeneratableAttachmentTypes?itemTypeId=' + itemRevision.item.itemTypeId, '-- Select attachment type');
    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- Select language');
    const documentOutputTypes = useSelectListOptions('api/v1/selectlist/DocumentOutputTypes', '-- Select output type');
    const outputOptions = useSelectListOptions('api/v1/selectlist/DocumentOutputOptions');


    let language = 'en-GB';
    if (user)
        language = user.language;

    const [formData, setFormData] = useState<ItemAttachmentGenerateModel>({
        billOfMaterialsId: 0,
        attachmentTypeId: 0,
        language: language,
        documentOutputTypeId: 3,
        outputOptions: 1,
    });

    useEffect(() => {
        if (boms) {
            formData.billOfMaterialsId = +boms[0].value;
        }
    }, [boms]);

    //console.log('boms', boms)

    function submit() {

        const url = `api/v1/ItemAttachments/Generate?billOfMaterialsId=${formData.billOfMaterialsId}&attachmentTypeId=${formData.attachmentTypeId}&language=${formData.language}&documentOutputTypeId=${formData.documentOutputTypeId}&documentOutputOptions=${formData.outputOptions}`
        setShowSpinner(true)
        DataService
            .getFile(url)
            .then(response => {
                setShowSpinner(false)
                console.log('response', response)
                if (response instanceof Blob) {
                    let filename = 'attachment'
                    if (attachmentTypes && attachmentTypes.filter(x => x.value == formData.attachmentTypeId).length > 0)
                        filename = attachmentTypes.filter(x => x.value == formData.attachmentTypeId)[0].label

                    filename += ` ${itemRevision.item.itemNr} v${itemRevision.revisionNumber}` 

                    const url = window.URL.createObjectURL(response);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', response.type === 'image/jpeg' ? filename + '.jpg' : filename + '.pdf');
                    document.body.appendChild(link);
                    link.click();

                    if (onHide)
                        onHide();
                }
                else {
                    handleResult(response)
                }

            })
            .catch(e => handleException(e));
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'billOfMaterialsId', label: 'Version', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
            { name: 'attachmentTypeId', label: 'Attachment type', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
            { name: 'language', label: 'Language', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
            { name: 'documentOutputTypeId', label: 'Output type', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
            { name: 'outputOptions', label: 'Options', labelSize: 4, validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <>
            <Popup show={show} onHide={onHide} size={'lg'} title={'Generate an attachment'} >
                <Popup.Body>
                    <RadioButtonListField {...getFieldProps('billOfMaterialsId')} options={boms} />
                    <SelectListField {...getFieldProps('attachmentTypeId')} options={attachmentTypes} />
                    <SelectListField {...getFieldProps('language')} options={languages} />
                    <SelectListField {...getFieldProps('documentOutputTypeId')} options={documentOutputTypes} />
                    <RadioButtonListField {...getFieldProps('outputOptions')} layout='vertical' options={outputOptions} />
                </Popup.Body>
                <Popup.Footer>
                    <SaveButton {...getSubmitButtonProps()} text='Generate' />
                </Popup.Footer>
            </Popup>
            { showSpinner && <SpinnerOverlay /> }
        </>
    );
}
export default GenerateAttachmentPopup;